import i18next, { i18n } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector, { CustomDetector } from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { configureYupLocale } from '@cbridge-utils/shared-helpers';
import text from './en/client.json';
import {
  defaultLanguage,
  fallbackLanguage,
  isLanguage,
  LANGUAGE_CACHE_KEY,
  localeLoaders,
  resolveLanguage,
  supportedLanguages,
} from './i18n.constant';

const NAMESPACE = 'text';

const loadLocale = async (language: string): Promise<typeof text> => {
  const loader = localeLoaders[`./${language}/client.json`];

  if (!loader) {
    throw new Error(`No dictionary for language "${language}"`);
  }

  return (await loader()).default;
};

/** The language last used in this browser; only an exact target counts, an outdated value (the source locale, a pruned language) is unset. */
const savedLanguageDetector: CustomDetector = {
  name: 'savedLanguage',
  lookup: () => {
    try {
      const saved = window.localStorage.getItem(LANGUAGE_CACHE_KEY);

      return isLanguage(saved) ? saved : undefined;
    } catch {
      return undefined; // Storage access denied: nothing saved.
    }
  },
};

/** Last resort of the detection; without it i18next would fall back to the source locale. */
const defaultLanguageDetector: CustomDetector = {
  name: 'defaultLanguage',
  lookup: () => defaultLanguage,
};

const createLanguageDetector = () => new LanguageDetector().addDetector(savedLanguageDetector).addDetector(defaultLanguageDetector);

export interface IInitI18nOptions {
  /** Fixed language for Storybook and tests; skips browser detection. */
  language?: string;
}

/** Resolves once the initial language is loaded; detection order: last choice in this browser → browser language → default. */
export const initI18n = async (options: IInitI18nOptions = {}): Promise<i18n> => {
  configureYupLocale();

  const instance = i18next.use(initReactI18next).use(resourcesToBackend(loadLocale));

  if (!options.language) {
    instance.use(createLanguageDetector());
  }

  await instance.init({
    lng: options.language,
    fallbackLng: fallbackLanguage,
    supportedLngs: supportedLanguages,
    load: 'currentOnly',
    ns: [NAMESPACE],
    defaultNS: NAMESPACE,
    // The source locale ships inline; the backend above serves the synced languages.
    partialBundledLanguages: true,
    resources: {
      [fallbackLanguage]: {
        [NAMESPACE]: text,
      },
    },
    debug: import.meta.env.DEV,
    detection: {
      order: [savedLanguageDetector.name, 'navigator', defaultLanguageDetector.name],
      caches: ['localStorage'],
      lookupLocalStorage: LANGUAGE_CACHE_KEY,
      // Browser languages are mapped onto the targets (en, en-US → en-GB); an unmatched code is skipped by i18next.
      convertDetectedLanguage: (language: string) => resolveLanguage(language) ?? language,
    },
    interpolation: {
      escapeValue: false, // React already escapes values
    },
  });

  return instance;
};
